import React, {
  useState, useEffect, useCallback,
} from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ReceiptLongOutlined as ExtIcon,
  DeleteOutline as DeleteIcon,
  CheckCircle as CheckIcon,
  RefreshRounded as RefreshIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { formatCurrency } from '../../utils/format';
import { confirmAsync } from '../../../../utils/functions/globalDialog';

// การ์ด "รายการที่ไม่มีใน payment ระบบ" หน้า company-account — แสดง Payment เงา (source:
// ocr_external) ที่ระบบสร้างจากรายการในรูปสเตทเมนต์ธนาคารที่จับคู่ใบเบิกในระบบไม่ได้
// กลุ่ม "รอระบุ" ด้านบน ให้ฝ่ายการเงินกรอกว่าเป็นรายการอะไร + ยืนยัน (สถานะในสมุดเปลี่ยนเป็น
// "ยืนยันแล้ว" + column รายการโชว์ชื่อที่กรอก) · กลุ่ม "ยืนยันแล้ว" ด้านล่างเป็นประวัติ
const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(`${String(v).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? v
    : d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
};

function DirChip({ direction, outlined }) {
  return (
    <Chip
      size="small"
      variant={outlined ? 'outlined' : 'filled'}
      color={direction === 'income' ? 'success' : 'error'}
      label={direction === 'income' ? 'เงินเข้า' : 'เงินออก'}
      sx={{ height: outlined ? 20 : 22 }}
    />
  );
}

function PendingRow({
  row, busy, onConfirm, onDelete,
}) {
  const [detail, setDetail] = useState('');
  const s = row.snapshot || {};

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: '#fde68a', bgcolor: '#fffbeb' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <DirChip direction={row.direction} />
            <Typography sx={{ fontWeight: 800 }}>{formatCurrency(row.amount)} บาท</Typography>
            <Typography variant="body2" color="text.secondary">
              {fmtDate(row.found_date)}
              {s.time ? ` ${s.time}` : ''}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
            จากรูป: {s.description || '(ไม่มีรายละเอียดในรูป)'}
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="ระบุว่าเป็นรายการอะไร"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          sx={{ minWidth: { xs: '100%', md: 260 } }}
        />
        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            variant="contained"
            startIcon={<CheckIcon />}
            disabled={busy || !detail.trim()}
            onClick={() => onConfirm(row.id, detail.trim())}
          >
            ยืนยัน
          </Button>
          <Tooltip title="ลบรายการนี้">
            <span>
              <IconButton size="small" color="error" disabled={busy} onClick={() => onDelete(row.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

export function ExternalPaymentsCard() {
  const dispatch = useDispatch();
  const [pending, setPending] = useState([]);
  const [confirmed, setConfirmed] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await dispatch(actions.listExternalPayments());
      setPending(data.pending || []);
      setConfirmed(data.confirmed || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConfirm = async (id, detail) => {
    setBusyId(id);
    try {
      await dispatch(actions.confirmExternalPayment({ id, detail }));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirmAsync('ลบรายการนอกระบบนี้? จะหายจากสมุดและยอดทันที');
    if (!ok) return;

    setBusyId(id);
    try {
      await dispatch(actions.deleteExternalPayment({ id }));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const total = pending.length + confirmed.length;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: '#e2e8f0', overflow: 'hidden' }}>
      <Box sx={{ p: { xs: 2, md: 2.5 }, borderBottom: '1px solid', borderColor: '#e2e8f0' }}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#fef2f2', color: '#b91c1c' }}>
              <ExtIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 750 }}>รายการที่ไม่มีใน payment ระบบ</Typography>
              <Typography variant="body2" color="text.secondary">
                รายการในรูปธนาคารที่จับคู่กับใบเบิกในระบบไม่ได้ — ระบุว่าเป็นรายการอะไรเพื่อยืนยัน
              </Typography>
            </Box>
          </Stack>
          <Tooltip title="โหลดใหม่">
            <span>
              <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid', borderColor: '#e2e8f0' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ minHeight: 160, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={26} />
        </Box>
      ) : total === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>ไม่มีรายการนอกระบบ</Typography>
          <Typography variant="body2" color="text.secondary">
            รายการในรูปที่ไม่มีในระบบจะมาแสดงที่นี่หลังยืนยัน OCR
          </Typography>
        </Box>
      ) : (
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          {pending.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#b45309' }}>
                รอระบุ ({pending.length})
              </Typography>
              <Stack spacing={1}>
                {pending.map((row) => (
                  <PendingRow
                    key={row.id}
                    row={row}
                    busy={busyId === row.id}
                    onConfirm={handleConfirm}
                    onDelete={handleDelete}
                  />
                ))}
              </Stack>
            </>
          )}
          {confirmed.length > 0 && (
            <>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mt: pending.length > 0 ? 2.5 : 0, mb: 1, color: '#15803d' }}
              >
                ยืนยันแล้ว ({confirmed.length})
              </Typography>
              <Stack spacing={0.75}>
                {confirmed.map((row) => {
                  const s = row.snapshot || {};
                  return (
                    <Paper key={row.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2, borderColor: '#d1fae5' }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <DirChip direction={row.direction} outlined />
                            <Typography sx={{ fontWeight: 700 }}>{row.detail}</Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(row.amount)} บาท · {fmtDate(row.found_date)} · จากรูป: {s.description || '-'}
                          </Typography>
                        </Box>
                        <Tooltip title="ลบรายการนี้">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={busyId === row.id}
                              onClick={() => handleDelete(row.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </>
          )}
        </Box>
      )}
    </Paper>
  );
}

export default ExternalPaymentsCard;
